import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Loader2, Trophy } from "lucide-react";
import { fetchAdminLeaderboardHistory } from "./api/fetchAdminLeaderboardHistory";
import {
  formatPeriodLabel,
  formatPrizeAmount,
  topWinners,
  weekTotals,
} from "./leaderboardHistoryHelpers";
import { formatScore, leaderDisplayName, memberInitials, PODIUM_META } from "./leaderboardHelpers";

function HistoryWinnerRow({ winner }) {
  const tone = PODIUM_META[winner.rank]?.tone || "rest";
  return (
    <div className={`alb-hist-row alb-hist-row-${tone}`}>
      <span className="alb-hist-rank tabular-nums">#{winner.rank}</span>
      <span className="alb-hist-avatar">{memberInitials(winner.name, winner.inviteCode)}</span>
      <span className="alb-hist-body">
        <strong>{leaderDisplayName(winner)}</strong>
        <em>
          {winner.userLevel || "Rookie"}
          {winner.inviteCode ? ` · ${winner.inviteCode}` : ""}
        </em>
      </span>
      <span className="alb-hist-score tabular-nums">{formatScore(winner.score)}</span>
      <span className="alb-hist-prize">
        <em className="alb-hist-prize-cash">{formatPrizeAmount(winner.prize?.CASH)} CASH</em>
        <em className="alb-hist-prize-coin">{formatPrizeAmount(winner.prize?.COIN)} COIN</em>
      </span>
    </div>
  );
}

function HistoryWeekCard({ snapshot, expanded, onToggle }) {
  const label = snapshot.periodLabel || formatPeriodLabel(snapshot.periodKey);
  const totals = weekTotals(snapshot);
  const podium = topWinners(snapshot, 3);

  return (
    <article className={`alb-hist-card${expanded ? " alb-hist-card-open" : ""}`}>
      <button type="button" className="alb-hist-card-btn" onClick={onToggle}>
        <span className="alb-hist-card-date">
          <Trophy size={13} aria-hidden />
          <span>
            <strong>{label}</strong>
            <em>{snapshot.periodKey}</em>
          </span>
        </span>

        <span className="alb-hist-card-podium">
          {podium.map((w) => (
            <span key={w.rank} className={`alb-hist-mini alb-hist-mini-${PODIUM_META[w.rank]?.tone || "rest"}`}>
              <em>#{w.rank}</em>
              <strong>{leaderDisplayName(w)?.split(" ")[0] || w.inviteCode || "—"}</strong>
            </span>
          ))}
        </span>

        <span className="alb-hist-card-totals">
          <em>{totals.winners} paid</em>
          <strong>{formatPrizeAmount(totals.cash)} CASH</strong>
          <strong>{formatPrizeAmount(totals.coin)} COIN</strong>
        </span>

        <ChevronDown size={14} className={`alb-hist-chevron${expanded ? " alb-hist-chevron-open" : ""}`} />
      </button>

      {expanded ? (
        <div className="alb-hist-card-sheet">
          {(snapshot.winners || [])
            .slice()
            .sort((a, b) => a.rank - b.rank)
            .map((winner) => (
              <HistoryWinnerRow key={`${snapshot.periodKey}-${winner.rank}`} winner={winner} />
            ))}
        </div>
      ) : null}
    </article>
  );
}

export default function LeaderboardHistoryPanel({
  refreshToken = 0,
  fetchHistory = fetchAdminLeaderboardHistory,
}) {
  const [snapshots, setSnapshots] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchHistory({ page, limit: 10 });
      setSnapshots(data?.snapshots || []);
      setPagination(data?.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      setSnapshots([]);
      setError(err.response?.data?.error || err.message || "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [fetchHistory]);

  useEffect(() => {
    load(1);
  }, [load, refreshToken]);

  return (
    <section className="alb-history" aria-label="Past leaderboard winners">
      <header className="alb-history-head">
        <div>
          <strong>Hall of fame</strong>
          <span>Past weekly winners and prizes · {pagination.total || 0} week{(pagination.total || 0) === 1 ? "" : "s"}</span>
        </div>
      </header>

      {error ? (
        <div className="alb-error" role="alert">
          {error}
          <button type="button" onClick={() => load(pagination.page || 1)}>
            Retry
          </button>
        </div>
      ) : null}

      {loading && !snapshots.length ? (
        <div className="alb-history-loading">
          <Loader2 size={18} className="animate-spin" aria-hidden />
          <span>Loading past winners…</span>
        </div>
      ) : null}

      {!loading && !error && !snapshots.length ? (
        <p className="alb-history-empty">
          No weekly snapshots yet. History is recorded automatically when the Sunday reward job runs.
        </p>
      ) : null}

      <div className="alb-history-list">
        {snapshots.map((snapshot) => (
          <HistoryWeekCard
            key={snapshot._id || snapshot.periodKey}
            snapshot={snapshot}
            expanded={expandedKey === snapshot.periodKey}
            onToggle={() =>
              setExpandedKey((prev) =>
                prev === snapshot.periodKey ? null : snapshot.periodKey
              )
            }
          />
        ))}
      </div>

      {pagination.totalPages > 1 ? (
        <footer className="alb-pager">
          <button
            type="button"
            disabled={!pagination.hasPrev || loading}
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            aria-label="Previous weeks"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="alb-pager-meta tabular-nums">
            {pagination.page} / {pagination.totalPages}
          </span>
          <button
            type="button"
            disabled={!pagination.hasNext || loading}
            onClick={() => load((pagination.page || 1) + 1)}
            aria-label="Next weeks"
          >
            <ChevronRight size={14} />
          </button>
        </footer>
      ) : null}
    </section>
  );
}
